import { Box, Flex, xcss } from '@atlaskit/primitives';
import { RovoThinkingBar } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBar';
import { RovoThinkingBarWrapper } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBarWrapper';
import { RovoThinkingReady } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingReady';
import { useLocale } from '@databases/intl';
import React, { useCallback, useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';

const fixedThinkingBarContainerStyles = xcss({
  position: 'fixed',
  bottom: '36px',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '336px',
  maxWidth: 'calc(100vw - 32px)',
  zIndex: 'modal'
});

const inlineThinkingBarContainerStyles = xcss({
  marginInline: 'auto',
  width: 'min(400px, 100%)'
});

const readyContainerStyles = xcss({
  width: '100%'
});

type DatabaseThinkingBarBaseProps = {
  status: 'analyzing' | 'editing';
  onStop: () => void;
  customRotatingMessages?: string[];
  startedAt?: number;
  placement?: 'fixed' | 'inline';
};

export type DatabaseThinkingBarProps = DatabaseThinkingBarBaseProps &
  (
    | {
        readyState?: undefined;
        isComplete?: boolean;
        onCompleteAnimationFinished?: () => void;
      }
    | {
        readyState: {
          isReady: boolean;
          onClose: () => void;
          readyText: string;
        };
        isComplete?: never;
        onCompleteAnimationFinished?: () => void;
      }
  );

export const DatabaseThinkingBar = ({
  status,
  onStop,
  customRotatingMessages,
  isComplete,
  onCompleteAnimationFinished,
  readyState,
  startedAt,
  placement = 'fixed'
}: DatabaseThinkingBarProps) => {
  const locale = useLocale();
  const isReady = readyState?.isReady ?? false;
  const [showReady, setShowReady] = useState(false);
  useEffect(() => {
    if (!isReady) {
      setShowReady(false);
    }
  }, [isReady]);
  const handleCompleteAnimationFinished = useCallback(() => {
    if (isReady) {
      setShowReady(true);
    }
    onCompleteAnimationFinished?.();
  }, [isReady, onCompleteAnimationFinished]);
  const thinkingBarProps = {
    status,
    onStop,
    showProgressBar: true,
    onCompleteAnimationFinished: readyState
      ? handleCompleteAnimationFinished
      : onCompleteAnimationFinished,
    useRotatingTitle: true,
    customRotatingMessages,
    startedAt
  };

  /**
   * The Rovo component and Databases use different react-intl contexts.
   */
  return (
    <IntlProvider locale={locale}>
      <Box
        xcss={
          placement === 'fixed'
            ? fixedThinkingBarContainerStyles
            : inlineThinkingBarContainerStyles
        }
        testId="database-thinking-bar"
      >
        <RovoThinkingBarWrapper isThinking={!showReady}>
          <Flex alignItems="center" justifyContent="space-between">
            {readyState && showReady ? (
              <Box xcss={readyContainerStyles}>
                <RovoThinkingReady
                  onClose={readyState.onClose}
                  readyText={readyState.readyText}
                />
              </Box>
            ) : (
              <RovoThinkingBar
                {...thinkingBarProps}
                isComplete={readyState ? isReady : isComplete}
              />
            )}
          </Flex>
        </RovoThinkingBarWrapper>
      </Box>
    </IntlProvider>
  );
};
